import React, { useState } from 'react';
import { FaLock, FaEye, FaCamera, FaPhone, FaShieldAlt, FaUserSecret,FaBell } from 'react-icons/fa';
import CustomDropdown from '../common/UserCustomDropdown';

const DashboardPrivacy = () => {
    const [privacySettings, setPrivacySettings] = useState({
        profileVisibility: 'all-members',
        photoPrivacy: 'visible-all',
        phoneVisibility: 'premium-members',
        secureConnect: true,
        showOnlineStatus: true,
        showProfileViews: true,
        emailVisibility: 'premium-members',
        locationSharing: 'city-only'
    });

    const profileVisibilityOptions = [
        { value: 'all-members', label: 'All Members' },
        { value: 'premium-only', label: 'Premium Members Only' },
        { value: 'shortlisted-only', label: 'Shortlisted Members Only' },
        { value: 'invite-only', label: 'Invitation Only' }
    ];

    const photoPrivacyOptions = [
        { value: 'visible-all', label: 'Visible to All' },
        { value: 'on-request', label: 'Visible on Request' },
        { value: 'protected', label: 'Protected (Password Required)' },
        { value: 'premium-only', label: 'Premium Members Only' }
    ];

    const phoneVisibilityOptions = [
        { value: 'premium-members', label: 'Premium Members' },
        { value: 'accepted-contacts', label: 'Only Contacts I Accept' },
        { value: 'nobody', label: 'Nobody' },
        { value: 'after-match', label: 'After Match Acceptance' }
    ];

    const emailVisibilityOptions = [
        { value: 'premium-members', label: 'Premium Members' },
        { value: 'accepted-contacts', label: 'Only Contacts I Accept' },
        { value: 'nobody', label: 'Nobody' },
        { value: 'system-only', label: 'System Messages Only' }
    ];

    const locationOptions = [
        { value: 'city-only', label: 'City Only' },
        { value: 'region-only', label: 'Region Only' },
        { value: 'country-only', label: 'Country Only' },
        { value: 'hide-location', label: 'Hide Location' }
    ];

    const handleToggle = (key) => {
        setPrivacySettings(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleDropdownChange = (key, value) => {
        setPrivacySettings(prev => ({ ...prev, [key]: value }));
    };

    const handleSave = () => {
        console.log('Saving:', privacySettings);
        alert('Privacy settings saved!');
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="w-full mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3">
                        <FaLock className="text-xl text-rose-600" />
                        <div>
                            <h2 className="text-2xl font-semibold text-gray-900">Privacy Settings</h2>
                            <p className="text-sm text-gray-600 mt-1">Manage your visibility and security</p>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="space-y-6">
                    {/* Privacy Controls */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                        <div className="p-6 border-b border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                                <FaShieldAlt className="text-rose-600" />
                                Profile & Visibility
                            </h3>
                        </div>
                        <div className="p-6 space-y-5">
                            <SettingRow
                                icon={<FaEye className="text-blue-600" />}
                                title="Profile Visibility"
                                description="Who can view your full profile"
                            >
                                <CustomDropdown
                                    options={profileVisibilityOptions}
                                    value={privacySettings.profileVisibility}
                                    onChange={(v) => handleDropdownChange('profileVisibility', v)}
                                />
                            </SettingRow>

                            <SettingRow
                                icon={<FaCamera className="text-pink-600" />}
                                title="Photo Privacy"
                                description="Control visibility of your photos"
                            >
                                <CustomDropdown
                                    options={photoPrivacyOptions}
                                    value={privacySettings.photoPrivacy}
                                    onChange={(v) => handleDropdownChange('photoPrivacy', v)}
                                />
                            </SettingRow>

                            <SettingRow
                                icon={<FaPhone className="text-green-600" />}
                                title="Phone Number"
                                description="Who can see your phone number"
                            >
                                <CustomDropdown
                                    options={phoneVisibilityOptions}
                                    value={privacySettings.phoneVisibility}
                                    onChange={(v) => handleDropdownChange('phoneVisibility', v)}
                                />
                            </SettingRow>

                            <SettingRow
                                icon={<FaBell className="text-orange-600" />}
                                title="Email Visibility"
                                description="Who can see your email address"
                            >
                                <CustomDropdown
                                    options={emailVisibilityOptions}
                                    value={privacySettings.emailVisibility}
                                    onChange={(v) => handleDropdownChange('emailVisibility', v)}
                                />
                            </SettingRow>

                            <SettingRow
                                icon={<FaUserSecret className="text-indigo-600" />}
                                title="Location Sharing"
                                description="How much location info to show"
                            >
                                <CustomDropdown
                                    options={locationOptions}
                                    value={privacySettings.locationSharing}
                                    onChange={(v) => handleDropdownChange('locationSharing', v)}
                                />
                            </SettingRow>
                        </div>
                    </div>

                    {/* SecureConnect */}
                    <div className=" text-rose-500 p-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                                    <FaShieldAlt />
                                </div>
                                <div>
                                    <h3 className="font-extrabold ">SecureConnect™</h3>
                                    <p className="text-sm text-black font-semibold">Call without revealing your number</p>
                                </div>
                            </div>
                            <Toggle
                                checked={privacySettings.secureConnect}
                                onChange={() => handleToggle('secureConnect')}
                            />
                        </div>
                    </div>

                    {/* Activity Status */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-5 flex items-center gap-2">
                            <FaUserSecret className="text-blue-600" />
                            Activity Status
                        </h3>
                        <div className="space-y-4">
                            <ToggleRow
                                title="Show when I'm online"
                                checked={privacySettings.showOnlineStatus}
                                onChange={() => handleToggle('showOnlineStatus')}
                            />
                            <ToggleRow
                                title="Show profile views"
                                description="Let others know when you viewed their profile"
                                checked={privacySettings.showProfileViews}
                                onChange={() => handleToggle('showProfileViews')}
                            />
                            <ToggleRow
                                title="Last seen status"
                                description="Show when you were last active"
                                checked={true} // hardcoded as in original
                                onChange={() => {}}
                                disabled
                            />
                        </div>
                    </div>
                </div>

                {/* Save Button */}
                <div className="mt-8 flex justify-end">
                    <button
                        onClick={handleSave}
                        className="px-6 py-3 bg-rose-600 text-white font-medium rounded-lg hover:bg-rose-700 transition shadow-md"
                    >
                        Save Settings
                    </button>
                </div>
            </div>
        </div>
    );
};

// Reusable Components
const SettingRow = ({ icon, title, description, children }) => (
    <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
            <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                {icon}
            </div>
            <div>
                <p className="font-medium text-gray-900">{title}</p>
                <p className="text-sm text-gray-500">{description}</p>
            </div>
        </div>
        <div className="w-64">{children}</div>
    </div>
);

const Toggle = ({ checked, onChange }) => (
    <label className="relative inline-flex items-center cursor-pointer">
        <input type="checkbox" className="sr-only peer" checked={checked} onChange={onChange} />
        <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-rose-600 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
    </label>
);

const ToggleRow = ({ title, description, checked, onChange, disabled }) => (
    <div className="flex items-center justify-between">
        <div>
            <p className="font-medium text-gray-900">{title}</p>
            {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
        </div>
        <Toggle checked={checked} onChange={onChange} disabled={disabled} />
    </div>
);

export default DashboardPrivacy;